/**
 * The entry composer (JRNL-07) — **one composer serves every capture path**:
 * the Project page's card, the journal's new-entry prompt, `Fill in` on a
 * catch-up gap, and backfilling a past day from the timeline.
 *
 * Three things in here are load-bearing and are not styling:
 *
 * 1. **The headcount stepper's initial and reachable state is "Not recorded",
 *    not 0.** Decrementing from 1 reaches Not recorded; 0 is reachable only by
 *    typing it, and means "nobody was on site" — a real and different claim.
 *    `Number("")` is `0`, and this codebase has shipped that bug once already
 *    (`data-model/0004` §3).
 * 2. **The daily-entry gate is `missingForSignOff` from the contract**, not a
 *    second opinion about what "complete" means. *"A screen that offers a
 *    sign-off the server refuses reads as a broken button."*
 * 3. **The primary label is `Save note` / `Save as daily entry`.** Nothing is
 *    generated in this release, and a label describing an intention the build
 *    cannot honour is worse than no label at all (§9).
 *
 * Dictation records in the browser and goes through the same server pipeline
 * as the phone's (`architecture/0019`). The transcript is added to Notes, and
 * what it suggests is reviewed in the same sections as a typed note's
 * (`journal-dictation.tsx`, `product/0014`).
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Cloud,
  CloudFog,
  CloudRain,
  Image as ImageIcon,
  Loader2,
  Mic,
  Minus,
  Plus,
  Sun,
  Wind,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  ExtractedDeliveryConditionSchema,
  ExtractedIncidentSeveritySchema,
  WEATHER_LABELS,
  WeatherSchema,
  EXTRACTED_DELIVERY_CONDITION_LABELS,
  EXTRACTED_INCIDENT_SEVERITY_LABELS_CAPTURE,
  formatMissingForSignOff,
  missingForSignOff,
  type ExtractedDelivery,
  type DictationClarification,
  type ExtractedIncident,
  type NoteExtractionSuggestion,
  type Weather,
} from "@gb/contracts";
import { SectionLabel } from "@/components/gb/section-label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { answerClarification, type DictationResult } from "./dictation-api";
import {
  ClarificationCards,
  DictationDialog,
  DictationStatus,
  type ClarificationAnswerInput,
} from "./journal-dictation";
import { useDictation } from "./use-dictation";
import { todayIso } from "./journal-calendar";
import { JournalPhotos } from "./journal-photos";
import { useOpenDay } from "./journal-photos-api";
import {
  journalKeys,
  useCreateJournalNote,
  useCrewCountOn,
  useExtractFromDraft,
  useJournalDays,
} from "./journals-api";

/** Matches `JournalNoteInputSchema.headcount`. */
const MAX_HEADCOUNT = 2000;

/** Decorative only — `WEATHER_LABELS` carries the meaning. */
const WEATHER_ICONS: Record<Weather, LucideIcon> = {
  clear: Sun,
  "partial-sun": Cloud,
  rain: CloudRain,
  "high-wind": Wind,
  fog: CloudFog,
};

/** Long enough to be a sentence. Below this there is nothing to read. */
const MIN_EXTRACT_CHARS = 25;

/**
 * Long enough that ordinary typing does not trigger a read, short enough that
 * the answer is there by the time somebody looks down at the sections.
 */
const EXTRACT_DEBOUNCE_MS = 900;

/**
 * Fold a fresh suggestion into what the user already has in front of them.
 *
 * Keyed on `sourceQuote`, which is both the provenance marker and a natural
 * identity for a model item: the same sentence read twice is the same finding,
 * so re-reading a draft after a typo fix does not duplicate every row.
 *
 * Two rules, and both exist to stop the model overwriting a person:
 *  - an item the user typed (no quote) is never touched;
 *  - a suggested item already on screen keeps the user's edits rather than
 *    being reset to what the model just said again.
 */
function mergeSuggested<T extends { sourceQuote: string | null }>(
  current: T[],
  suggested: T[],
): T[] {
  const seen = new Set(current.map((item) => item.sourceQuote).filter(Boolean));
  return [
    ...current,
    ...suggested.filter(
      (item) => item.sourceQuote && !seen.has(item.sourceQuote),
    ),
  ];
}

export function JournalComposer({
  projectId,
  date,
  open,
  onOpenChange,
  onSaved,
}: {
  projectId: string;
  date: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (result: { noteId: string; replacedDailyEntry: boolean }) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          {/* Inter, sentence case — the dialog kit's own convention, and
              deliberately not Oswald (`ui/dialog.tsx`). */}
          <DialogTitle>New note</DialogTitle>
          <DialogCloseButton />
        </DialogHeader>
        {/* Remounted when the composer is opened for a different date, so the
            fields initialise from it rather than needing an effect to copy a
            prop into state. The portal unmounts on close, so a plain close and
            reopen resets everything by construction. */}
        <ComposerForm
          key={date}
          projectId={projectId}
          date={date}
          onOpenChange={onOpenChange}
          onSaved={onSaved}
        />
      </DialogContent>
    </Dialog>
  );
}

function ComposerForm({
  projectId,
  date,
  onOpenChange,
  onSaved,
}: {
  projectId: string;
  date: string;
  onOpenChange: (open: boolean) => void;
  onSaved?: (result: { noteId: string; replacedDailyEntry: boolean }) => void;
}) {
  const today = todayIso();
  const ids = useId();

  /**
   * This note's id, minted here and sent with it.
   *
   * One per open composer, which is what makes it right: the portal unmounts on
   * close (see the `key={date}` note above), so every draft gets a fresh one,
   * and a save retried after a timeout re-uses the id of the draft still on
   * screen rather than filing a second note. The phone needs this for its
   * outbox; the browser gets the same protection for free.
   */
  const [noteId] = useState(() => crypto.randomUUID());

  const [selectedDate, setSelectedDate] = useState(date);
  /** `null` is **not recorded**, and is where this field starts. Never 0. */
  const [headcount, setHeadcount] = useState<number | null>(null);
  /** Once the user has touched it, no suggestion overwrites their answer. */
  const [touchedHeadcount, setTouchedHeadcount] = useState(false);
  const [fromCrew, setFromCrew] = useState(false);
  const [weather, setWeather] = useState<Weather | null>(null);
  const [body, setBody] = useState("");
  const [isDailyEntry, setIsDailyEntry] = useState(false);
  const [dictateOpen, setDictateOpen] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  /**
   * The day the photo panel is filing against, once it has been opened.
   *
   * Carries the date it was opened for, not just the id: the composer lets the
   * date be changed while it is open, and a stale entry id would file today's
   * photographs against yesterday without saying so.
   */
  const [photoDay, setPhotoDay] = useState<{
    date: string;
    entryId: string;
  } | null>(null);
  /**
   * Photographs uploaded in *this* sitting, in order, by id.
   *
   * The panel shows these and nothing else, so a new note starts empty however
   * many pictures the day already holds — from the phone, or from an earlier
   * note. They are claimed for the note when it is filed; until then they are
   * on the day, which is where they stay if the note is abandoned.
   */
  const [notePhotoIds, setNotePhotoIds] = useState<string[]>([]);
  const notePhotos = useMemo(() => new Set(notePhotoIds), [notePhotoIds]);

  /**
   * What the user has agreed to — not what the model said.
   *
   * Seeded from the suggestion and then owned by the user: every field is
   * editable, every row is removable, and rows can be added by hand. Only what
   * survives here is filed. Extraction pre-fills a form, a human confirms it,
   * and only the confirmation counts (`document-ai/0002`).
   */
  const [deliveries, setDeliveries] = useState<ExtractedDelivery[]>([]);
  const [incidents, setIncidents] = useState<ExtractedIncident[]>([]);
  /** Verbatim, for `extracted_raw` — kept even where the user overrode it. */
  const [extractedRaw, setExtractedRaw] =
    useState<NoteExtractionSuggestion | null>(null);
  /**
   * Whether a model has actually read this draft yet.
   *
   * Distinct from "the lists are empty": empty because nothing read the note
   * and empty because the note mentions no deliveries are different facts, and
   * only the second is a claim about the site. The section copy says which.
   */
  const [modelRan, setModelRan] = useState(false);
  /** The body the suggestion belongs to, so a stale answer cannot land. */
  const askedFor = useRef<string | null>(null);
  /**
   * The recording this note was dictated from, sent with it so the retained
   * audio stays joined to the record it produced. The latest take wins when
   * there are several — one column, the phone's rule.
   */
  const [dictationId, setDictationId] = useState<string | null>(null);
  const [clarifications, setClarifications] = useState<DictationClarification[]>([]);

  const crew = useCrewCountOn(projectId, selectedDate);
  const queryClient = useQueryClient();
  const openDay = useOpenDay(projectId);

  /**
   * Open the day, then show its photographs.
   *
   * A photograph belongs to a *day*, and an unflagged note never writes the
   * `journal_entries` row a day is — which is why this panel used to be a
   * paragraph explaining that photos go somewhere else. But `openDay` is
   * idempotent precisely so a picture can be filed against a date nobody has
   * written up yet, so the day is simply opened here. Notes almost always come
   * with photographs; sending someone away to file the note first and come back
   * was asking them to work around the schema.
   *
   * On demand rather than when the composer opens, so cancelling a note the
   * user never photographed does not leave an empty day behind.
   */
  const openPhotos = async (): Promise<void> => {
    setPhotosOpen(true);
    if (photoDay?.date === selectedDate) return;
    const entry = await openDay.mutateAsync(selectedDate).catch(() => null);
    if (entry) setPhotoDay({ date: selectedDate, entryId: entry.id });
  };
  // One date's summary, for the third hint line: whether flagging this note
  // would *replace* a record the date already has, and clear its sign-off.
  const summary = useJournalDays(projectId, {
    from: selectedDate,
    to: selectedDate,
  });
  const daySummary = summary.data?.days.find(
    (day) => day.date === selectedDate,
  );
  /** The day's, not this note's — photographs are filed against the date. */
  const dayPhotoCount = daySummary?.photoCount ?? 0;
  /** The date already holds a flagged note, which this one would replace. */
  const replacesExisting = daySummary?.hasDailyEntry ?? false;
  /**
   * The date carries a signature.
   *
   * Independent of `replacesExisting`, because sign-off is a separate
   * deliberate act: most days holding a flagged note have never been approved,
   * and telling those users their sign-off is being cleared would be false in
   * the majority case — in the one line §7.8 calls the main affordance.
   */
  const dateSignedOff = daySummary?.signedOff ?? false;
  /**
   * Signed off with **no note behind it** — somebody wrote the day up in the
   * existing day form and signed it there.
   *
   * The API clears the sign-off on `replaced || day.approved`, so this case
   * loses a signature too, and it is the one that loses it most quietly:
   * `replacedDailyEntry` stays `false` because no note was replaced, so the
   * flag cannot be used to detect it. `signedOff` without `hasDailyEntry` is
   * exactly the distinction, and it is already on the summary.
   */
  const clearsUnbackedSignOff = dateSignedOff && !replacesExisting;

  const create = useCreateJournalNote(projectId);
  const { mutateAsync: runExtract } = useExtractFromDraft(projectId);

  /**
   * Read the draft once it stops changing.
   *
   * Debounced rather than run on every keystroke, and rather than only on
   * save: the point of running it here is that the answer is on screen while
   * the note is, so it can be corrected. Running it at save time would put the
   * suggestion in front of somebody who has already left.
   *
   * A suggestion only lands if the body still matches what was asked about —
   * a slow answer for text since rewritten would attach a reading to prose it
   * was never made from, which is `document-ai/0005`'s transfer failure in
   * another costume.
   */
  useEffect(() => {
    const draft = body.trim();
    if (draft.length < MIN_EXTRACT_CHARS) return;
    if (askedFor.current === draft) return;

    const timer = setTimeout(() => {
      askedFor.current = draft;
      runExtract(draft)
        .then((suggestion) => {
          if (askedFor.current !== draft) return;
          setModelRan(suggestion.modelRan);
          setExtractedRaw(suggestion);
          // Merges rather than replaces: anything hand-added or already
          // corrected survives a later re-read. Model items are keyed by the
          // sentence they came from, so re-reading the same prose does not
          // duplicate them.
          setDeliveries((current) =>
            mergeSuggested(current, suggestion.deliveries),
          );
          setIncidents((current) =>
            mergeSuggested(current, suggestion.incidents),
          );
        })
        .catch(() => {
          // Suggestions are a convenience. Losing them must never stop
          // somebody filing what they wrote.
          if (askedFor.current === draft) setModelRan(false);
        });
    }, EXTRACT_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // `runExtract` is `mutateAsync`, which TanStack keeps stable across
    // renders — so this re-arms when the draft changes and not otherwise.
    // Depending on the mutation *object* would re-arm every render and the
    // debounce would never fire.
  }, [body, runExtract]);

  useEffect(() => {
    if (touchedHeadcount) return;
    const suggestion = crew.data;
    // The response echoes the date back precisely so a late answer for a date
    // the user has since changed cannot silently pre-fill the wrong day.
    if (!suggestion || suggestion.date !== selectedDate) return;
    // A crew count of 0 pre-fills **nothing**. Zero people assigned is not the
    // same claim as "nobody was on site", and only the second one is a 0 in a
    // headcount cell.
    if (suggestion.count <= 0) return;
    setHeadcount(suggestion.count);
    setFromCrew(true);
  }, [crew.data, selectedDate, touchedHeadcount]);

  /**
   * What the pipeline heard, added to Notes. The transcript is the speaker's
   * own words and appends rather than replaces.
   *
   * Its suggestion is taken only when the transcript is the whole note. The
   * suggestion was read from the transcript alone, and the rule above is that
   * a reading only lands on the text it was made from. When there was already
   * text, the debounced read covers the combined note instead — taking both
   * would put two readings of one sentence on screen.
   *
   * Headcount and weather are taken either way, unless the person already set
   * them: a number somebody *said* is stated, not inferred (the phone's
   * reasoning), and those two are not keyed to a sentence.
   */
  const applyDictation = (result: DictationResult): void => {
    setDictateOpen(false);
    setDictationId(result.dictationId);
    setClarifications(result.clarifications);

    const before = body.trim();
    const next = before ? `${before}\n\n${result.transcript.trim()}` : result.transcript.trim();
    setBody(next);

    const suggestion = result.suggestion;
    if (!suggestion) return;
    if (suggestion.headcount !== null && !touchedHeadcount) {
      setHeadcount(suggestion.headcount);
      setTouchedHeadcount(true);
      setFromCrew(false);
    }
    if (suggestion.weather !== null && weather === null) setWeather(suggestion.weather);
    if (before) return;
    askedFor.current = next;
    setModelRan(suggestion.modelRan);
    setExtractedRaw(suggestion);
    setDeliveries((current) => mergeSuggested(current, suggestion.deliveries));
    setIncidents((current) => mergeSuggested(current, suggestion.incidents));
  };

  const dictation = useDictation({ projectId, date: selectedDate, onResult: applyDictation });

  /**
   * Optimistic: the click shows at once and the corpus write follows. The
   * server's copy replaces it only while this is still the same dictation; a
   * failed write keeps the optimistic state — an answer is never worth an
   * error.
   */
  const answerQuestion = (clarificationId: string, answer: ClarificationAnswerInput): void => {
    const forDictation = dictationId;
    if (!forDictation) return;
    setClarifications((current) =>
      current.map((question) =>
        question.id === clarificationId
          ? {
              ...question,
              answer: {
                chosenIndex: "chosenIndex" in answer ? answer.chosenIndex : null,
                typedText: "typedText" in answer ? answer.typedText : null,
                answeredAt: new Date().toISOString(),
              },
            }
          : question,
      ),
    );
    answerClarification({ projectId, dictationId: forDictation, clarificationId, answer })
      .then((fresh) => {
        if (fresh) {
          setClarifications((current) =>
            current.some((question) => question.id === clarificationId) ? fresh : current,
          );
        }
      })
      .catch(() => undefined);
  };

  const changeDate = (next: string): void => {
    // Future dates are not selectable. The native picker is capped as well;
    // this is the half that survives a typed value.
    if (!next || next > today) return;
    setSelectedDate(next);
    if (!touchedHeadcount) {
      setHeadcount(null);
      setFromCrew(false);
    }
  };

  const editHeadcount = (next: number | null): void => {
    setTouchedHeadcount(true);
    setFromCrew(false);
    setHeadcount(next);
  };

  const typeHeadcount = (raw: string): void => {
    const trimmed = raw.trim();
    // Blank stays blank. `Number("")` is 0, and filing "nobody on site" for a
    // field somebody never reached is the exact bug that made these columns
    // nullable in the first place.
    if (trimmed === "") return editHeadcount(null);
    if (!/^\d+$/.test(trimmed)) return;
    editHeadcount(Math.min(MAX_HEADCOUNT, Number(trimmed)));
  };

  const trimmedBody = body.trim();
  // The contract's own function, so this gate and the server's `approve` derive
  // the same answer from the same code. Reimplementing it is how a screen ends
  // up offering a sign-off the server refuses.
  const missing = missingForSignOff({
    headcount,
    weather,
    workCompleted: trimmedBody || null,
  });
  const gated = isDailyEntry && missing.length > 0;

  // §9's five states. `hasDailyEntry` picks the replace branch over the
  // unbacked one — a note really is being replaced, which is the more specific
  // fact — and `signedOff` picks the wording within it.
  const hint = !isDailyEntry
    ? "Saves as a note. Deliveries and incidents still get logged."
    : replacesExisting
      ? dateSignedOff
        ? "Replaces the day’s existing record, and clears its sign-off."
        : "Replaces the day’s existing record."
      : clearsUnbackedSignOff
        ? "This date is signed off. Saving will clear that sign-off and file this note as the day’s record."
        : "Files this as the day’s record for this date — your headcount, weather and notes become the entry.";

  /**
   * The warning follows the consequence, not the state count: it is exactly
   * "this date carries a signature that saving will take off".
   *
   * Replacing a record nobody signed is ordinary editing and is **not** warned
   * about. Warning there would train people to look past the warning in the two
   * states where it costs them something, which is the failure §7.8's "main
   * affordance" line exists to prevent.
   */
  const clearsSignOff = isDailyEntry && dateSignedOff;

  const save = async (asDailyEntry: boolean): Promise<void> => {
    if (!trimmedBody) return;
    const result = await create.mutateAsync({
      id: noteId,
      date: selectedDate,
      // The console composer writes words. `no-work` is filed from the mobile
      // gap card in one tap and never passes through a composer.
      kind: "note",
      body: trimmedBody,
      // `null`, never 0.
      headcount,
      weather,
      isDailyEntry: asDailyEntry,
      // What survived the user's review, not what the model returned.
      deliveries,
      incidents,
      // The raw suggestion goes too, so `extracted_raw` records what was
      // proposed even where every field of it was overwritten.
      extractedRaw,
      // Uploaded before this note had an id, so they are claimed by it now.
      photoIds: notePhotoIds,
      dictationId,
    });
    onSaved?.({
      noteId: result.note.id,
      replacedDailyEntry: result.replacedDailyEntry,
    });
    onOpenChange(false);
  };

  const busy = create.isPending;

  return (
    <>
      <DialogBody className="flex flex-col gap-5">
        {/* 1 · Date -------------------------------------------------------- */}
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <label
              htmlFor={`${ids}-date`}
              className="font-display text-[0.72rem] font-semibold tracking-[0.09em] text-ink-slate uppercase"
            >
              Date
            </label>
            <span className="inline-flex items-center rounded-full border-[1.5px] border-border bg-subtle px-3.5 py-1.5">
              <input
                id={`${ids}-date`}
                type="date"
                value={selectedDate}
                max={today}
                onChange={(event) => changeDate(event.target.value)}
                className="cursor-pointer border-none bg-transparent font-mono text-[0.84rem] text-ink outline-none"
              />
            </span>
          </div>
          <DialogDescription className="mt-2">
            Pick a past date to backfill a note
          </DialogDescription>
        </div>

        {/* 2 · Headcount --------------------------------------------------- */}
        <div>
          <SectionLabel className="mb-2">On site</SectionLabel>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center rounded-input border-[1.5px] border-border bg-surface">
              <button
                type="button"
                aria-label="One fewer on site"
                // Nothing below Not recorded, so this is off at the bottom
                // rather than silently doing nothing.
                disabled={headcount === null}
                onClick={() =>
                  // From 1 — and from a typed 0 — this reaches **Not
                  // recorded**, not 0.
                  editHeadcount(
                    headcount === null || headcount <= 1 ? null : headcount - 1,
                  )
                }
                className="flex size-10 cursor-pointer items-center justify-center rounded-l-input text-ink-slate transition-colors hover:bg-subtle disabled:cursor-not-allowed disabled:text-faint disabled:hover:bg-transparent"
              >
                <Minus size={16} />
              </button>
              <input
                id={`${ids}-headcount`}
                inputMode="numeric"
                aria-label="On site"
                value={headcount === null ? "" : String(headcount)}
                // The visible "not recorded" state. An em dash or a 0 in this
                // box would both read as something somebody entered.
                placeholder="Not recorded"
                onChange={(event) => typeHeadcount(event.target.value)}
                className="w-[124px] border-x-[1.5px] border-border bg-surface py-2.5 text-center font-mono text-[0.88rem] text-ink outline-none placeholder:font-sans placeholder:text-[0.82rem] placeholder:text-faint"
              />
              <button
                type="button"
                aria-label="One more on site"
                onClick={() =>
                  editHeadcount(
                    headcount === null
                      ? 1
                      : Math.min(MAX_HEADCOUNT, headcount + 1),
                  )
                }
                className="flex size-10 cursor-pointer items-center justify-center rounded-r-input text-ink-slate transition-colors hover:bg-subtle"
              >
                <Plus size={16} />
              </button>
            </div>
            {/* Not recorded has to be reachable from *any* value, and thirty
                clicks on the decrement is not reachable. */}
            <Button
              variant="ghost"
              size="sm"
              type="button"
              disabled={headcount === null}
              onClick={() => editHeadcount(null)}
            >
              Not recorded
            </Button>
          </div>
          {fromCrew && (
            <p className="mt-2 text-[0.8rem] leading-[1.5] text-muted">
              Suggested from the crew assigned to this site that day — a
              suggestion, not a record of who turned up.
            </p>
          )}
        </div>

        {/* 3 · Weather ----------------------------------------------------- */}
        <div>
          <SectionLabel className="mb-2">Weather</SectionLabel>
          {/* The five options and their labels come from the contract, so this
              screen and the day form cannot disagree about either. */}
          {/* Five equal tiles with an icon above the label, as the design has
              them — not pills. The icon is decorative: it is never the only
              thing carrying the meaning, because five small grey glyphs are
              not distinguishable at a glance and "Fog" is. */}
          <div
            role="group"
            aria-label="Weather"
            className="grid grid-cols-5 gap-2"
          >
            {WeatherSchema.options.map((option) => {
              const selected = weather === option;
              const Icon = WEATHER_ICONS[option];
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={selected}
                  // Deselectable back to none: weather is nullable, and a note
                  // filed at 07:40 about a delivery need not claim it.
                  onClick={() => setWeather(selected ? null : option)}
                  className={cn(
                    "flex cursor-pointer flex-col items-center gap-1.5 rounded-input border-[1.5px] px-2 py-2.5",
                    "font-display text-[0.72rem] font-semibold tracking-[0.02em]",
                    "transition-colors",
                    selected
                      ? "border-brand bg-brand-tint text-brand-on-tint"
                      : "border-border bg-surface text-ink-slate hover:border-border-hover hover:bg-subtle",
                  )}
                >
                  <Icon aria-hidden className="size-[18px]" />
                  {WEATHER_LABELS[option]}
                </button>
              );
            })}
          </div>
        </div>

        {/* 4 · Notes ------------------------------------------------------- */}
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <SectionLabel>Notes</SectionLabel>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => setDictateOpen(true)}
            >
              <Mic size={15} />
              Dictate
            </Button>
          </div>
          <Textarea
            id={`${ids}-body`}
            aria-label="Notes"
            rows={6}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="Dictate or type — a delivery, a hold-up, a near-miss, or the whole day…"
          />
          {!dictateOpen && (
            <DictationStatus phase={dictation.phase} onOpen={() => setDictateOpen(true)} />
          )}
        </div>

        <ClarificationCards questions={clarifications} onAnswer={answerQuestion} />

        {/* 5 · The daily-entry flag ---------------------------------------- */}
        <div
          className={cn(
            "rounded-input border-[1.5px] px-3.5 py-3 transition-colors",
            isDailyEntry
              ? "border-brand bg-brand-tint"
              : "border-border bg-surface",
          )}
        >
          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={isDailyEntry}
              aria-describedby={`${ids}-hint`}
              onChange={(event) => setIsDailyEntry(event.target.checked)}
              className="size-4 shrink-0 accent-brand"
            />
            <span className="text-[0.9rem] font-semibold text-ink">
              This note is the daily entry
            </span>
          </label>
          {/* The main affordance in the modal: it is where the user learns what
              saving will do. §9 owns every word of it. */}
          <p
            id={`${ids}-hint`}
            className={cn(
              "mt-1.5 text-[0.82rem] leading-[1.5]",
              clearsSignOff ? "font-semibold text-warn-text" : "text-ink-prose",
            )}
          >
            {hint}
          </p>
        </div>

        {gated && (
          <p
            role="alert"
            className="rounded-input border-[1.5px] border-warn/40 bg-warn-bg px-3.5 py-2.5 text-[0.82rem] leading-[1.5] font-semibold text-warn-text"
          >
            Add {formatMissingForSignOff(missing)} before this can be the day’s
            record. It can still be saved as a note.
          </p>
        )}

        {/* Photographs still attach to the *day* rather than to this note —
            that is the grain the schema has, and `document-ai/0004` explains
            why. What changed is that the day is now opened on demand instead of
            the user being told to file the note and come back. The count is the
            day's, for the same reason. */}
        <section className="space-y-2">
          <SectionLabel
            trailing={
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={openDay.isPending}
                onClick={() => void openPhotos()}
              >
                <ImageIcon aria-hidden className="size-4" />
                {openDay.isPending ? "Opening…" : "Add photos"}
              </Button>
            }
          >
            Photos
          </SectionLabel>
          <p className="text-[0.85rem] text-muted">
            {notePhotoIds.length > 0
              ? `${notePhotoIds.length} ${notePhotoIds.length === 1 ? "photo" : "photos"} on this note${
                  dayPhotoCount > notePhotoIds.length
                    ? ` · ${dayPhotoCount} on the day`
                    : ""
                }.`
              : "No photos on this note — attach the ones that show what you are describing."}
          </p>
        </section>

        <SuggestedSection
          label="Deliveries"
          empty={
            modelRan
              ? "None yet — mention one in the note and it appears here."
              : "Nothing has read this note yet. Mention a delivery and it appears here."
          }
          onAdd={() =>
            setDeliveries((current) => [
              ...current,
              {
                supplier: null,
                item: null,
                qty: null,
                condition: "unknown",
                sourceQuote: null,
              },
            ])
          }
          items={deliveries}
          onRemove={(index) =>
            setDeliveries((current) => current.filter((_, i) => i !== index))
          }
          render={(delivery, index) => (
            <div className="grid gap-2">
              <Input
                aria-label="Supplier"
                placeholder="Supplier"
                value={delivery.supplier ?? ""}
                onChange={(event) =>
                  setDeliveries((current) =>
                    current.map((item, i) =>
                      i === index
                        ? { ...item, supplier: blank(event.target.value) }
                        : item,
                    ),
                  )
                }
              />
              <Input
                aria-label="Item"
                placeholder="Item"
                value={delivery.item ?? ""}
                onChange={(event) =>
                  setDeliveries((current) =>
                    current.map((item, i) =>
                      i === index
                        ? { ...item, item: blank(event.target.value) }
                        : item,
                    ),
                  )
                }
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  aria-label="Quantity"
                  placeholder="Quantity"
                  value={delivery.qty ?? ""}
                  onChange={(event) =>
                    setDeliveries((current) =>
                      current.map((item, i) =>
                        i === index
                          ? { ...item, qty: blank(event.target.value) }
                          : item,
                      ),
                    )
                  }
                />
                <select
                  aria-label="Condition"
                  className="h-11 rounded-input border-[1.5px] border-border bg-surface px-3 text-[0.9rem] text-ink"
                  value={delivery.condition}
                  onChange={(event) =>
                    setDeliveries((current) =>
                      current.map((item, i) =>
                        i === index
                          ? {
                              ...item,
                              condition: ExtractedDeliveryConditionSchema.parse(
                                event.target.value,
                              ),
                            }
                          : item,
                      ),
                    )
                  }
                >
                  {ExtractedDeliveryConditionSchema.options.map((option) => (
                    <option key={option} value={option}>
                      {CONDITION_LABELS[option]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        />

        <SuggestedSection
          label="Incidents / near-misses"
          empty={
            modelRan
              ? "None yet — mention one in the note and it appears here."
              : "Nothing has read this note yet. Mention an incident and it appears here."
          }
          onAdd={() =>
            setIncidents((current) => [
              ...current,
              { severity: "unknown", text: null, sourceQuote: null },
            ])
          }
          items={incidents}
          onRemove={(index) =>
            setIncidents((current) => current.filter((_, i) => i !== index))
          }
          render={(incident, index) => (
            <div className="grid gap-2">
              <Input
                aria-label="What happened"
                placeholder="What happened"
                value={incident.text ?? ""}
                onChange={(event) =>
                  setIncidents((current) =>
                    current.map((item, i) =>
                      i === index
                        ? { ...item, text: blank(event.target.value) }
                        : item,
                    ),
                  )
                }
              />
              <select
                aria-label="Severity"
                className="h-11 rounded-input border-[1.5px] border-border bg-surface px-3 text-[0.9rem] text-ink"
                value={incident.severity}
                onChange={(event) =>
                  setIncidents((current) =>
                    current.map((item, i) =>
                      i === index
                        ? {
                            ...item,
                            severity: ExtractedIncidentSeveritySchema.parse(
                              event.target.value,
                            ),
                          }
                        : item,
                    ),
                  )
                }
              >
                {ExtractedIncidentSeveritySchema.options.map((option) => (
                  <option key={option} value={option}>
                    {SEVERITY_LABELS[option]}
                  </option>
                ))}
              </select>
            </div>
          )}
        />

        {create.error && (
          <p
            role="alert"
            className="rounded-input border-[1.5px] border-danger/40 bg-danger-bg px-3.5 py-2.5 text-[0.82rem] font-semibold text-danger"
          >
            {create.error.message}
          </p>
        )}
      </DialogBody>

      <DialogFooter>
        <Button
          variant="secondary"
          size="footer"
          type="button"
          disabled={busy}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        {/* The gate stops the *flag*, never the note. */}
        {gated && (
          <Button
            variant="secondary"
            size="footer"
            type="button"
            disabled={!trimmedBody || busy}
            onClick={() => void save(false)}
          >
            Save note
          </Button>
        )}
        <Button
          size="footer"
          type="button"
          className="hover:bg-brand-deep"
          disabled={!trimmedBody || gated || busy}
          onClick={() => void save(isDailyEntry)}
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          {isDailyEntry ? "Save as daily entry" : "Save note"}
        </Button>
      </DialogFooter>

      <DictationDialog
        open={dictateOpen}
        onOpenChange={setDictateOpen}
        dictation={dictation}
      />

      <Dialog
        open={photosOpen}
        onOpenChange={(open) => {
          setPhotosOpen(open);
          // The day's summary carries the photo count this composer shows, and
          // the feed behind it shows the same number. Neither is invalidated by
          // an upload, which only knows about the photo list.
          if (!open && photoDay) {
            void queryClient.invalidateQueries({
              queryKey: journalKeys.list(projectId),
            });
          }
        }}
      >
        <DialogContent overlayClassName="z-65">
          <DialogHeader>
            <DialogTitle>Photos — {selectedDate}</DialogTitle>
            <DialogCloseButton />
          </DialogHeader>
          <DialogBody>
            {photoDay?.date === selectedDate ? (
              <>
                <DialogDescription className="mb-3">
                  Attached to this note when you file it, and to the day either
                  way — so they show what you are describing without leaving the
                  site record.
                </DialogDescription>
                <JournalPhotos
                  projectId={projectId}
                  entryId={photoDay.entryId}
                  only={notePhotos}
                  onFiled={(photoId) =>
                    setNotePhotoIds((current) =>
                      current.includes(photoId)
                        ? current
                        : [...current, photoId],
                    )
                  }
                />
              </>
            ) : openDay.isError ? (
              <p
                role="alert"
                className="text-[0.85rem] font-semibold text-danger"
              >
                {openDay.error.message}
              </p>
            ) : (
              <p
                className="flex items-center gap-2 text-[0.85rem] text-muted"
                role="status"
                aria-live="polite"
              >
                <Loader2 aria-hidden className="size-4 animate-spin" />
                Opening the day…
              </p>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Both controls ask a person a question, so both take the contract's *capture*
// rendering rather than the record's. That is what changes `none` from the
// "No harm" this file used to say to "Near-miss": an incident row exists only
// because somebody chose to record something, so `none` never meant "nothing
// happened" here. MOB-15, and the pairing is spelled out beside the maps in
// `@gb/contracts`.
const CONDITION_LABELS = EXTRACTED_DELIVERY_CONDITION_LABELS;

const SEVERITY_LABELS = EXTRACTED_INCIDENT_SEVERITY_LABELS_CAPTURE;

/** Empty means "not stated", never an empty string in a column. */
function blank(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * A section of model suggestions the user owns.
 *
 * Each row that came from the model carries a `FROM NOTE` pill and the
 * sentence it was read out of, quoted, so the reading can be checked against
 * the words without leaving the modal. Rows without a quote were typed here
 * and carry no pill — the difference is the whole point, because a derived
 * value and a stated one must not look alike on a record that gets signed off.
 */
function SuggestedSection<T extends { sourceQuote: string | null }>({
  label,
  empty,
  items,
  onAdd,
  onRemove,
  render,
}: {
  label: string;
  empty: string;
  items: T[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  render: (item: T, index: number) => ReactNode;
}) {
  return (
    <section className="space-y-2">
      <SectionLabel
        trailing={
          <Button type="button" variant="outline" size="sm" onClick={onAdd}>
            <Plus aria-hidden className="size-4" />
            Add
          </Button>
        }
      >
        {label}
      </SectionLabel>

      {items.length === 0 ? (
        <p className="text-[0.85rem] text-muted">{empty}</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((item, index) => (
            <li
              key={index}
              className={cn(
                "rounded-card border-[1.5px] p-3",
                item.sourceQuote
                  ? "border-brand-border bg-brand-tint-3"
                  : "border-border bg-surface",
              )}
            >
              <div className="mb-2 flex items-start gap-2">
                {item.sourceQuote && (
                  <>
                    <Badge tone="brand" shape="pill">
                      From note
                    </Badge>
                    <q className="min-w-0 flex-1 text-[0.82rem] leading-[1.45] text-ink-prose">
                      {item.sourceQuote}
                    </q>
                  </>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="ml-auto shrink-0"
                  aria-label={`Remove this ${label.toLowerCase()} entry`}
                  onClick={() => onRemove(index)}
                >
                  <X aria-hidden className="size-4" />
                </Button>
              </div>
              {render(item, index)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
